import type { InvoiceFilters } from '@/services/invoices';
import type { InspectionFilters } from '@/services/inspections';
import type { NotificationFilters } from '@/services/notifications';
import type { AuditFilters, AuditExportFilters } from '@/services/audit';
import type { PropertyFilters } from '@/services/properties';
import type { CorrespondenceFilters } from '@/services/correspondences';
import type { SupportTicketFilters } from '@/services/supportTickets';

/**
 * Central registry of all React Query keys.
 *
 * Rules:
 *  - `all` is the broadest scope (invalidates everything for the domain).
 *  - `lists()` narrows to list-type queries.
 *  - `list(filters)` narrows further by filter combination.
 *  - `detail(id)` targets a single entity.
 */
export const QUERY_KEYS = {
  users: {
    all: ['users'] as const,
    lists: () => [...QUERY_KEYS.users.all, 'list'] as const,
    detail: (id: number) => [...QUERY_KEYS.users.all, 'detail', id] as const,
    profile: () => [...QUERY_KEYS.users.all, 'profile'] as const,
    onboarding: () => [...QUERY_KEYS.users.all, 'onboarding'] as const,
    invite: (token: string) => [...QUERY_KEYS.users.all, 'invite', token] as const,
  },

  invoices: {
    all: ['invoices'] as const,
    lists: () => [...QUERY_KEYS.invoices.all, 'list'] as const,
    list: (filters?: InvoiceFilters) => [...QUERY_KEYS.invoices.lists(), filters] as const,
    detail: (id: number) => [...QUERY_KEYS.invoices.all, 'detail', id] as const,
    byProperty: (propertyId: number) =>
      [...QUERY_KEYS.invoices.all, 'property', propertyId] as const,
  },

  inspections: {
    all: ['inspections'] as const,
    lists: () => [...QUERY_KEYS.inspections.all, 'list'] as const,
    list: (filters?: InspectionFilters) => [...QUERY_KEYS.inspections.lists(), filters] as const,
    detail: (id: number) => [...QUERY_KEYS.inspections.all, 'detail', id] as const,
    mine: () => [...QUERY_KEYS.inspections.all, 'mine'] as const,
    byProperty: (propertyId: number) =>
      [...QUERY_KEYS.inspections.all, 'property', propertyId] as const,
    byUser: (userId: number) => [...QUERY_KEYS.inspections.all, 'user', userId] as const,
    upcoming: () => [...QUERY_KEYS.inspections.all, 'upcoming'] as const,
    stats: () => [...QUERY_KEYS.inspections.all, 'stats'] as const,
    inventory: (inspectionId: number) =>
      [...QUERY_KEYS.inspections.all, 'inventory', inspectionId] as const,
  },

  notifications: {
    all: ['notifications'] as const,
    lists: () => [...QUERY_KEYS.notifications.all, 'list'] as const,
    list: (filters?: NotificationFilters) =>
      [...QUERY_KEYS.notifications.lists(), filters] as const,
    preferences: () => [...QUERY_KEYS.notifications.all, 'preferences'] as const,
    unreadCount: () => [...QUERY_KEYS.notifications.all, 'unread-count'] as const,
    deliveryLogs: (params?: Record<string, unknown>) =>
      [...QUERY_KEYS.notifications.all, 'delivery-logs', params] as const,
  },

  audit: {
    all: ['audit'] as const,
    lists: () => [...QUERY_KEYS.audit.all, 'list'] as const,
    list: (filters?: AuditFilters) => [...QUERY_KEYS.audit.lists(), filters] as const,
    detail: (id: number) => [...QUERY_KEYS.audit.all, 'detail', id] as const,
    userActivity: (userId: number) => [...QUERY_KEYS.audit.all, 'user-activity', userId] as const,
    stats: () => [...QUERY_KEYS.audit.all, 'stats'] as const,
    export: (filters?: AuditExportFilters) => [...QUERY_KEYS.audit.all, 'export', filters] as const,
    health: () => [...QUERY_KEYS.audit.all, 'health'] as const,
  },

  reporting: {
    all: ['reporting'] as const,
    properties: () => [...QUERY_KEYS.reporting.all, 'properties'] as const,
    userActivity: () => [...QUERY_KEYS.reporting.all, 'user-activity'] as const,
    systemUsage: () => [...QUERY_KEYS.reporting.all, 'system-usage'] as const,
    monthlyStats: () => [...QUERY_KEYS.reporting.all, 'monthly-stats'] as const,
    agentPerformance: () => [...QUERY_KEYS.reporting.all, 'agent-performance'] as const,
    financialSummary: () => [...QUERY_KEYS.reporting.all, 'financial-summary'] as const,
  },

  organisations: {
    all: ['organisations'] as const,
    lists: () => [...QUERY_KEYS.organisations.all, 'list'] as const,
    list: () => [...QUERY_KEYS.organisations.lists()] as const,
    detail: (id: number) => [...QUERY_KEYS.organisations.all, 'detail', id] as const,
  },

  payments: ['payments'] as const,

  tenants: {
    all: ['tenants'] as const,
    profile: () => [...(['tenants'] as const), 'profile'] as const,
    list: (status?: string) => [...(['tenants'] as const), 'list', status] as const,
    detail: (id: number) => [...(['tenants'] as const), 'detail', id] as const,
  },

  leases: {
    all: ['leases'] as const,
    lists: () => [...QUERY_KEYS.leases.all, 'list'] as const,
    list: (status?: string) => [...QUERY_KEYS.leases.lists(), status] as const,
    detail: (id: number) => [...QUERY_KEYS.leases.all, 'detail', id] as const,
    my: () => [...QUERY_KEYS.leases.all, 'my'] as const,
  },

  maintenance: {
    all: ['maintenance'] as const,
    workOrders: () => [...QUERY_KEYS.maintenance.all, 'work-orders'] as const,
    workOrderList: (status?: string, propertyId?: number) =>
      [...QUERY_KEYS.maintenance.workOrders(), status, propertyId] as const,
    workOrderDetail: (id: number) =>
      [...QUERY_KEYS.maintenance.all, 'work-orders', 'detail', id] as const,
    vendorOrders: (status?: string) => [...QUERY_KEYS.maintenance.all, 'vendor', status] as const,
    myOrders: (status?: string) => [...QUERY_KEYS.maintenance.all, 'my', status] as const,
    rules: (propertyId?: number) => [...QUERY_KEYS.maintenance.all, 'rules', propertyId] as const,
  },

  crm: {
    all: ['crm'] as const,
    leads: () => [...QUERY_KEYS.crm.all, 'leads'] as const,
    leadDetail: (id: number) => [...QUERY_KEYS.crm.all, 'leads', 'detail', id] as const,
    opportunities: () => [...QUERY_KEYS.crm.all, 'opportunities'] as const,
    opportunityList: (status?: string, assignedToId?: string) =>
      [...QUERY_KEYS.crm.opportunities(), status, assignedToId] as const,
    opportunityDetail: (id: number) => [...QUERY_KEYS.crm.all, 'opportunities', 'detail', id] as const,
    offers: (opportunityId: number) => [...QUERY_KEYS.crm.all, 'opportunities', opportunityId, 'offers'] as const,
    staleListings: () => [...QUERY_KEYS.crm.all, 'stale-listings'] as const,
    insights: () => [...QUERY_KEYS.crm.all, 'insights'] as const,
  },

  assignments: {
    all: ['assignments'] as const,
    list: () => [...QUERY_KEYS.assignments.all, 'list'] as const,
    agents: () => [...QUERY_KEYS.assignments.all, 'agents'] as const,
    myScope: () => [...QUERY_KEYS.assignments.all, 'my-scope'] as const,
    forProperty: (id: number) => [...QUERY_KEYS.assignments.all, 'property', id] as const,
    forBuilding: (id: number) => [...QUERY_KEYS.assignments.all, 'building', id] as const,
  },

  ownerPortal: {
    all: ['owner-portal'] as const,
    dashboard: () => [...QUERY_KEYS.ownerPortal.all, 'dashboard'] as const,
    properties: () => [...QUERY_KEYS.ownerPortal.all, 'properties'] as const,
    invoices: () => [...QUERY_KEYS.ownerPortal.all, 'invoices'] as const,
    payouts: () => [...QUERY_KEYS.ownerPortal.all, 'payouts'] as const,
    buildings: () => [...QUERY_KEYS.ownerPortal.all, 'buildings'] as const,
    leases: () => [...QUERY_KEYS.ownerPortal.all, 'leases'] as const,
    maintenance: () => [...QUERY_KEYS.ownerPortal.all, 'maintenance'] as const,
    inspections: () => [...QUERY_KEYS.ownerPortal.all, 'inspections'] as const,
  },

  serviceCharges: {
    all: ['service-charges'] as const,
    budgets: () => [...QUERY_KEYS.serviceCharges.all, 'budgets'] as const,
    budgetDetail: (id: number) =>
      [...QUERY_KEYS.serviceCharges.all, 'budgets', 'detail', id] as const,
    variance: (budgetId: number) =>
      [...QUERY_KEYS.serviceCharges.all, 'variance', budgetId] as const,
    propertyRate: (propertyId: number) =>
      [...QUERY_KEYS.serviceCharges.all, 'property-rate', propertyId] as const,
  },

  documentTemplates: {
    all: ['document-templates'] as const,
    lists: () => [...QUERY_KEYS.documentTemplates.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...QUERY_KEYS.documentTemplates.lists(), filters] as const,
    detail: (id: number) => [...QUERY_KEYS.documentTemplates.all, 'detail', id] as const,
  },

  properties: {
    all: ['properties'] as const,
    lists: () => [...QUERY_KEYS.properties.all, 'list'] as const,
    list: (filters?: PropertyFilters) => [...QUERY_KEYS.properties.lists(), filters] as const,
    detail: (id: number) => [...QUERY_KEYS.properties.all, 'detail', id] as const,
    media: (id: number) => [...QUERY_KEYS.properties.all, 'media', id] as const,
  },

  correspondences: {
    all: ['correspondences'] as const,
    lists: () => [...QUERY_KEYS.correspondences.all, 'list'] as const,
    list: (filters?: CorrespondenceFilters) =>
      [...QUERY_KEYS.correspondences.lists(), filters] as const,
    detail: (id: number) => [...QUERY_KEYS.correspondences.all, 'detail', id] as const,
  },

  tenantApplications: {
    all: ['tenant-applications'] as const,
    lists: () => [...QUERY_KEYS.tenantApplications.all, 'list'] as const,
    list: (status?: string) => [...QUERY_KEYS.tenantApplications.lists(), status] as const,
    detail: (id: number) => [...QUERY_KEYS.tenantApplications.all, 'detail', id] as const,
    public: (token: string) => [...QUERY_KEYS.tenantApplications.all, 'public', token] as const,
    landlordPending: () => [...QUERY_KEYS.tenantApplications.all, 'landlord-pending'] as const,
    landlordHistory: () => [...QUERY_KEYS.tenantApplications.all, 'landlord-history'] as const,
    mine: () => [...QUERY_KEYS.tenantApplications.all, 'mine'] as const,
  },

  buildings: {
    all: ['buildings'] as const,
    lists: () => [...QUERY_KEYS.buildings.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...QUERY_KEYS.buildings.lists(), filters] as const,
    detail: (id: number) => [...QUERY_KEYS.buildings.all, 'detail', id] as const,
    units: (buildingId: number) => [...QUERY_KEYS.buildings.detail(buildingId), 'units'] as const,
    documents: (buildingId: number) =>
      [...QUERY_KEYS.buildings.detail(buildingId), 'documents'] as const,
    maintenance: (buildingId: number) =>
      [...QUERY_KEYS.buildings.detail(buildingId), 'maintenance'] as const,
    serviceCharge: (buildingId: number) =>
      [...QUERY_KEYS.buildings.detail(buildingId), 'service-charge'] as const,
    invoices: (buildingId: number) =>
      [...QUERY_KEYS.buildings.detail(buildingId), 'invoices'] as const,
  },

  devices: {
    all: ['devices'] as const,
    list: () => [...QUERY_KEYS.devices.all, 'list'] as const,
  },

  supportTickets: {
    all: ['supportTickets'] as const,
    lists: () => [...QUERY_KEYS.supportTickets.all, 'list'] as const,
    mine: (filters?: SupportTicketFilters) => [...QUERY_KEYS.supportTickets.lists(), 'mine', filters] as const,
    admin: (filters?: SupportTicketFilters) => [...QUERY_KEYS.supportTickets.lists(), 'admin', filters] as const,
  },

  pricingCampaigns: {
    all: ['pricingCampaigns'] as const,
    lists: () => [...QUERY_KEYS.pricingCampaigns.all, 'list'] as const,
    active: () => [...QUERY_KEYS.pricingCampaigns.all, 'active'] as const,
  },
};
